import {
    DeviceType,
    getSearchParams,
    getUserAgentInfo,
    IllegalArgumentError,
    type ReadonlyRecord,
} from '@amzn/vinyl-util'
import type { KeyMap } from '@/device/input/KeyMap'
import type { AmznStreamingOptions } from '@amzn/vinyl-amzn'

export type DeviceTypeName =
    | 'BigScreenDemo'
    | 'Comcast'
    | 'Hisense'
    | 'LgTv'
    | 'Samsung'
    | 'SamsungTv'
    | 'Sky'
    | 'VegaTv'
    | 'Vizio'
    | 'WebPlayer'
    | 'WebPlayerMobile'
    | 'Xbox'

export interface DeviceConfig {
    /**
     * The type of device, e.g. 'smarttv', 'desktop', 'mobile', 'wearable'
     * This can differ from the user agent detected device type; when a device type id is set, the
     * device type is explicit and not inferred from user agent.
     */
    readonly deviceType: DeviceType

    /**
     * The Device Type ID, or DTID, used by Amazon backend services for conditional allow lists.
     */
    readonly deviceTypeId: string

    /**
     * Use MAP Authentication, typically with Vega devices.
     */
    readonly usesMapAuth?: boolean

    /**
     * Key mappings.
     */
    readonly keyMap: KeyMap

    /**
     * If true, arrow keys will navigate spatially.
     */
    readonly useSpatialNavigation: boolean

    /**
     * Explicitly enables or disables HD/UHD settings.
     */
    readonly streamingOptions: AmznStreamingOptions

    /**
     * True if this device uses native HLS streaming instead of Dash.
     * Native HLS streaming does not give us control over ABR or streaming quality settings.
     */
    readonly hls: boolean
}

export const defaultKeyBindings = {
    down: 'ArrowDown',
    enter: 'Enter',
    left: 'ArrowLeft',
    right: 'ArrowRight',
    up: 'ArrowUp',
    back: 'GoBack',
    escape: 'Escape',
    playPause: 'MediaPlayPause',
    fastForward: ['MediaFastForward', 'BracketRight'],
    rewind: ['MediaRewind', 'BracketLeft'],
} as const satisfies KeyMap

export const defaultConfig = {
    keyMap: defaultKeyBindings,
    useSpatialNavigation: true,
    streamingOptions: {
        uhdEnabled: false,
        hdEnabled: false,
    },
    hls: false,
} as const satisfies Partial<DeviceConfig>

// Vinyl provides this but defining it here lets us lazy-load the player.
const hls = typeof WebKitMediaKeys !== 'undefined'

export const deviceConfigs = {
    Comcast: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A3AUT5UO72X6S8',
    },
    Sky: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A1G6GPHNENL8X1',
    },
    Xbox: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A9AIM1JKFYHZW',
    },
    Vizio: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A3QT3Y2HH9BSDB',
    },
    Hisense: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A27F3L5WMHDOWD',
    },
    Samsung: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A15SKKXQB6RPU4',
    },
    BigScreenDemo: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A3DNCSDVBULX7K',
    },
    SamsungTv: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A1CH8LNF5OMH4W',
    },
    LgTv: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A3LA286UB8D94R',
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
    },
    VegaTv: {
        ...defaultConfig,
        deviceType: DeviceType.SMART_TV,
        deviceTypeId: 'A1TGF7GBNNZ9EE',
        usesMapAuth: true,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
    },
    WebPlayer: {
        ...defaultConfig,
        deviceTypeId: 'A16ZV8BU3SN1N3',
        deviceType: DeviceType.DESKTOP,
        useSpatialNavigation: false,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
        hls,
    },
    WebPlayerMobile: {
        ...defaultConfig,
        deviceTypeId: 'A34OIAVFALWTNH',
        deviceType: DeviceType.MOBILE,
        useSpatialNavigation: false,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
        hls,
    },
} as const satisfies ReadonlyRecord<DeviceTypeName, DeviceConfig>

let deviceConfig: DeviceConfig | undefined

export function getDeviceConfig(): DeviceConfig {
    if (!deviceConfig) {
        const isMobile = getUserAgentInfo().device?.type === DeviceType.MOBILE
        const deviceTypeName =
            getSearchParams().get('device') ?? (isMobile ? 'WebPlayerMobile' : 'WebPlayer')
        if (!(deviceTypeName in deviceConfigs)) {
            throw new IllegalArgumentError('Unknown device type: ' + deviceTypeName)
        }
        deviceConfig = deviceConfigs[deviceTypeName as DeviceTypeName]
    }
    return deviceConfig
}

export function isMobile(): boolean {
    return getDeviceConfig().deviceType === DeviceType.MOBILE
}

export function isDesktop(): boolean {
    return getDeviceConfig().deviceType === DeviceType.DESKTOP
}

/**
 * A convenience reference to the device config isTv
 */
export function isTv(): boolean {
    return getDeviceConfig().deviceType === DeviceType.SMART_TV
}
